import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  SectionCard, EmptyState, ActionButton, LoadingState, StatusBadge, Pill, TimelineItem, ClientWorkspaceLink,
} from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { STATUS_TONES, usd, fmtDate } from "./common";
import { useCurrencyContext, equivalentFor } from "@/lib/currency";
import { FxDisclaimer } from "@/components/shared/DualPrice";

export default function EstimateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prop, setProp] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [agree, setAgree] = useState(false);
  const [acceptedBy, setAcceptedBy] = useState("");
  const [busy, setBusy] = useState(false);
  const fx = useCurrencyContext(prop?.client_id || prop?.client?.id);
  const eq = (v) => equivalentFor(v, prop?.currency || "USD", { rate: fx.usd_thb_rate, showThb: fx.show_thb });

  const load = useCallback(async () => {
    try {
      const [p, h] = await Promise.all([api.get(`/sales/proposals/${id}`), api.get(`/sales/proposals/${id}/history`)]);
      setProp(p.data); setHistory(h.data || []); setAcceptedBy(p.data.client?.full_name || "");
    } catch (e) { toast.error("Could not load estimate"); navigate("/sales/estimates"); } finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); } finally { setBusy(false); } };

  const copyLink = async () => {
    if (!prop.accept_token) return toast.error("Send the estimate first to generate a link");
    const url = `${window.location.origin}/p/${prop.accept_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Acceptance link copied");
    } catch {
      toast.info("Copy this acceptance link:", { description: url, duration: 10000 });
    }
  };

  const doAccept = async () => {
    if (!agree) return toast.error("Confirm agreement to the estimated charges");
    await act(() => api.post(`/sales/proposals/${id}/accept`, { agree: true, acceptance_method: "staff_assisted", accepted_by: acceptedBy }), "Estimate accepted");
    setAcceptOpen(false);
  };

  if (loading) return <LoadingState label="Loading estimate…" />;
  if (!prop) return null;
  const locked = prop.locked || prop.status === "accepted";

  return (
    <div data-testid="estimate-detail-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/sales/estimates")} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="estimate-back"><Icons.ArrowLeft className="h-4 w-4" /></button>
          {(prop.client_id || prop.client?.id) && (
            <button onClick={() => navigate(`/client-workspace/${prop.client_id || prop.client?.id}`)}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-indigo-700 border border-slate-200 rounded-lg px-2.5 py-1.5 transition-colors"
              data-testid="proposal-open-workspace">
              <Icons.User className="h-3.5 w-3.5" />Client Workspace
            </button>
          )}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-semibold text-slate-900">{prop.proposal_number}</h1>
              <StatusBadge status={prop.status} map={STATUS_TONES} />
              {locked && <Pill tone="neutral"><Icons.Lock className="h-3 w-3 mr-1 inline" />Locked</Pill>}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <ClientWorkspaceLink clientId={prop.client_id || prop.client?.id} clientName={prop.client?.full_name} clientNumber={prop.client?.client_number} testId="proposal-client-link" />
              {prop.title && <><span className="text-slate-300">·</span><span className="text-sm text-slate-500">{prop.title}</span></>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!locked && prop.status === "draft" && <ActionButton variant="secondary" icon="ShieldCheck" loading={busy} onClick={() => act(() => api.post(`/sales/proposals/${id}/status`, { status: "approved" }), "Approved")} data-testid="estimate-approve">Approve</ActionButton>}
          {!locked && ["draft", "internal_review", "approved"].includes(prop.status) && <ActionButton icon="Send" loading={busy} onClick={() => act(() => api.post(`/sales/proposals/${id}/send`, { base_url: window.location.origin }), "Estimate sent")} data-testid="estimate-send">Send</ActionButton>}
          {!locked && ["sent", "viewed", "reminder_sent"].includes(prop.status) && <ActionButton variant="secondary" icon="BellRing" loading={busy} onClick={() => act(() => api.post(`/sales/proposals/${id}/remind`, { status: "manual", base_url: window.location.origin }), "Reminder sent")} data-testid="estimate-remind">Send Reminder</ActionButton>}
          {prop.accept_token && <ActionButton variant="secondary" icon="Link" onClick={copyLink} data-testid="estimate-copy-link">Copy Accept Link</ActionButton>}
          {!locked && ["sent", "viewed", "reminder_sent", "approved"].includes(prop.status) && <ActionButton variant="secondary" icon="CheckCircle" onClick={() => setAcceptOpen(true)} data-testid="estimate-accept-open">Accept (Staff)</ActionButton>}
          {!locked && !["declined", "converted"].includes(prop.status) && <ActionButton variant="danger" icon="XCircle" loading={busy} onClick={() => act(() => api.post(`/sales/proposals/${id}/decline`), "Declined")} data-testid="estimate-decline">Decline</ActionButton>}
          {locked && <ActionButton variant="secondary" icon="Copy" loading={busy} onClick={() => act(async () => { const { data } = await api.post(`/sales/proposals/${id}/duplicate`); navigate(`/sales/estimates/${data.id}`); }, "Duplicated")} data-testid="estimate-duplicate">Duplicate / Revise</ActionButton>}
        </div>
      </div>

      {prop.engagement_prepared && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5 flex items-center gap-3" data-testid="engagement-prepared-banner">
          <Icons.CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <div><div className="font-medium text-emerald-800">Accepted — engagement creation prepared</div><div className="text-sm text-emerald-700">The engagement letter will be generated in the Engagement build.</div></div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <SectionCard title="Pricing" icon="Receipt">
            <div className="border border-slate-100 rounded-lg divide-y divide-slate-50">
              {prop.line_items.map((li) => (
                <div key={li.id} className="flex justify-between px-3 py-2.5 text-sm">
                  <div><div className="text-slate-800">{li.name} <span className="text-slate-400">×{li.quantity}</span></div>{li.description && <div className="text-xs text-slate-400">{li.description}</div>}</div>
                  <span className="inline-flex flex-col items-end leading-tight"><span className="font-medium">{usd(li.amount, prop.currency)}</span>{eq(li.amount) && <span className="text-[11px] text-slate-400">≈ {eq(li.amount)}</span>}</span>
                </div>))}
            </div>
            <div className="flex flex-col items-end gap-1 mt-3 text-sm">
              <div className="text-slate-500">Subtotal <span className="font-medium text-slate-800 ml-3">{usd(prop.subtotal, prop.currency)}</span>{eq(prop.subtotal) && <span className="text-[11px] text-slate-400 ml-1">≈ {eq(prop.subtotal)}</span>}</div>
              {prop.discount > 0 && <div className="text-slate-500">Discount <span className="font-medium text-rose-600 ml-3">-{usd(prop.discount, prop.currency)}</span></div>}
              <div className="text-base font-semibold text-slate-900">Total <span className="ml-3">{usd(prop.total, prop.currency)}</span>{eq(prop.total) && <span className="text-[11px] text-slate-400 ml-2 font-normal">≈ {eq(prop.total)}</span>}</div>
            </div>
            {(eq(prop.total)) && <FxDisclaimer className="mt-2 text-right" />}
            {prop.payment_terms && <p className="text-xs text-slate-400 mt-3 italic">{prop.payment_terms}</p>}
          </SectionCard>

          {(prop.document_body || prop.document_subject) && (
            <SectionCard title="Cover Letter" icon="Mail" data-testid="proposal-cover-letter">
              {prop.document_subject && <div className="text-sm font-medium text-slate-800 mb-2">{prop.document_subject}</div>}
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{prop.document_body}</p>
            </SectionCard>
          )}
          {prop.scope_summary && <SectionCard title="Scope Summary" icon="FileText"><p className="text-sm text-slate-700 whitespace-pre-wrap">{prop.scope_summary}</p></SectionCard>}
          {prop.client_notes && <SectionCard title="Client Notes" icon="MessageSquare"><p className="text-sm text-slate-700 whitespace-pre-wrap">{prop.client_notes}</p></SectionCard>}
        </div>

        <div className="space-y-5">
          <SectionCard title="Details" icon="Info">
            <Detail label="Client" value={prop.client?.full_name} />
            <Detail label="Status" value={<StatusBadge status={prop.status} map={STATUS_TONES} />} />
            <Detail label="Sent" value={fmtDate(prop.sent_at)} />
            <Detail label="Viewed" value={fmtDate(prop.viewed_at)} />
            <Detail label="Accepted" value={fmtDate(prop.accepted_at)} />
            <Detail label="Method" value={prop.acceptance_method ? <span className="capitalize">{prop.acceptance_method.replace(/_/g, " ")}</span> : "—"} />
            <Detail label="Expires" value={fmtDate(prop.expiration_date)} />
          </SectionCard>
          <SectionCard title="History" icon="Clock">
            {history.length === 0 ? <EmptyState icon="Clock" title="No history" /> :
              <div className="space-y-4">
                {history.map((h, i, arr) => (
                  <TimelineItem key={h.id} icon="dot" title={<span className="capitalize">{h.event.replace(/_/g, " ")}</span>} description={h.detail} meta={`${h.actor_name} · ${new Date(h.created_at).toLocaleString()}`} last={i === arr.length - 1} />
                ))}
              </div>}
          </SectionCard>
          {(prop.revisions || []).length > 0 && (
            <SectionCard title="Revision History" icon="GitCommit" data-testid="estimate-revisions">
              <div className="space-y-4">
                {prop.revisions.map((r) => {
                  const cur = Object.keys(r.changes?.per_currency || {})[0];
                  const pc = cur ? r.changes.per_currency[cur] : null;
                  const due = pc?.estimated_total_due_at_engagement;
                  const fmtDelta = (d) => (d > 0 ? `+${usd(d, cur)}` : usd(d, cur));
                  return (
                    <div key={r.id} className="border border-slate-100 rounded-lg p-3 text-sm" data-testid={`estimate-revision-${r.revision_number}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-800">Revision #{r.revision_number}</span>
                        <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{r.staff_name}</div>
                      {r.reason && <div className="text-xs text-slate-600 mt-1 italic">“{r.reason}”</div>}
                      {due && <div className="mt-2 text-slate-700">Due at Engagement: <span className="text-slate-400 line-through">{usd(due.old, cur)}</span> → <span className="font-semibold">{usd(due.new, cur)}</span> <span className={due.delta >= 0 ? "text-rose-600" : "text-emerald-600"}>({fmtDelta(due.delta)})</span></div>}
                      {(r.changes?.services_added || []).map((s, i) => <div key={`a${i}`} className="text-xs text-emerald-700 mt-1">+ Added: {s.name} ({usd(s.price, cur)})</div>)}
                      {(r.changes?.services_removed || []).map((s, i) => <div key={`rm${i}`} className="text-xs text-rose-700 mt-1">− Removed: {s.name}</div>)}
                      {(r.changes?.price_changes || []).map((s, i) => <div key={`p${i}`} className="text-xs text-slate-600 mt-1">↕ {s.name}: {usd(s.old_price, cur)} → {usd(s.new_price, cur)}</div>)}
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}
        </div>
      </div>

      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent data-testid="staff-accept-dialog">
          <DialogHeader><DialogTitle className="font-heading">Accept on behalf of client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-slate-500">Accepted by</label><input value={acceptedBy} onChange={(e) => setAcceptedBy(e.target.value)} data-testid="accept-by" className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
            <label className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg p-3">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" data-testid="accept-agree" />
              <span className="font-medium">I AGREE TO THE ESTIMATED CHARGES.</span>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <ActionButton variant="secondary" onClick={() => setAcceptOpen(false)}>Cancel</ActionButton>
            <ActionButton loading={busy} onClick={doAccept} data-testid="accept-confirm">Confirm Acceptance</ActionButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value }) {
  return <div className="flex justify-between gap-4 py-2 border-b border-slate-50 last:border-0"><span className="text-sm text-slate-500">{label}</span><span className="text-sm font-medium text-slate-800 text-right">{value || "—"}</span></div>;
}
